/** Faction and character definitions — 9 nations, 2 characters each. */

import type { BuildingType, UnitType, TurnWorld, PlayerId } from './types';
import { BUILDING_DEFS } from './buildings';
import { UNIT_DEFS } from './units';

export interface FactionCharacter {
  id: 'a' | 'b';
  name: string;
  portrait?: string;
}

export interface Faction {
  id: string;
  name: string;
  characters: [FactionCharacter, FactionCharacter];
}

/** Emoji flag prefixes for faction dropdown. */
export const FACTION_FLAGS: Record<string, string> = {
  polska: '🇵🇱',
  'iii-rzesza': '🇩🇪',
  francja: '🇫🇷',
  zsrr: '☭',
  usa: '🇺🇸',
  wlochy: '🇮🇹',
  japonia: '🇯🇵',
  jugoslawia: '🇷🇸',
  'wielka-brytania': '🇬🇧',
};

export const FACTIONS: Faction[] = [
  {
    id: 'polska',
    name: 'polska',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/maria-sklodowska-curie.png' },
      { id: 'b', name: 'b', portrait: '/portraits/jan-czochralski.png' },
    ],
  },
  {
    id: 'iii-rzesza',
    name: 'iii-rzesza',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/fritz-haber.png' },
      { id: 'b', name: 'b', portrait: '/portraits/karl-landsteiner.png' },
    ],
  },
  {
    id: 'francja',
    name: 'francja',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/jean-perrin.png' },
      { id: 'b', name: 'b', portrait: '/portraits/alexis-carrel.png' },
    ],
  },
  {
    id: 'zsrr',
    name: 'zsrr',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/konstantin-tsiolkovsky.png' },
      { id: 'b', name: 'b', portrait: '/portraits/ivan-pavlov.png' },
    ],
  },
  {
    id: 'usa',
    name: 'usa',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/thomas-edison.png' },
      { id: 'b', name: 'b', portrait: '/portraits/robert-goddard.png' },
    ],
  },
  {
    id: 'wlochy',
    name: 'wlochy',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/guglielmo-marconi.png' },
      { id: 'b', name: 'b', portrait: '/portraits/vito-volterra.png' },
    ],
  },
  {
    id: 'japonia',
    name: 'japonia',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/hideyo-noguchi.png' },
      { id: 'b', name: 'b', portrait: '/portraits/shibasaburo-kitasato.png' },
    ],
  },
  {
    id: 'jugoslawia',
    name: 'jugoslawia',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/nikola-tesla.png' },
      { id: 'b', name: 'b', portrait: '/portraits/mihajlo-pupin.png' },
    ],
  },
  {
    id: 'wielka-brytania',
    name: 'wielka-brytania',
    characters: [
      { id: 'a', name: 'a', portrait: '/portraits/ernest-rutherford.png' },
      { id: 'b', name: 'b', portrait: '/portraits/joseph-john-thomson.png' },
    ],
  },
];

/**
 * Pixel bounding boxes for each flag in flags.png (1672×941).
 * Measured from actual image content boundaries.
 */
const SHEET_W = 1672;
const SHEET_H = 941;

export const FLAG_SHEET = { url: '/flags.png', w: SHEET_W, h: SHEET_H };

export const FLAG_RECTS: Record<string, { x: number; y: number; w: number; h: number }> = {
  polska: { x: 201, y: 57, w: 349, h: 201 },
  'iii-rzesza': { x: 651, y: 57, w: 348, h: 201 },
  zsrr: { x: 1101, y: 57, w: 347, h: 201 },
  francja: { x: 201, y: 341, w: 349, h: 204 },
  japonia: { x: 651, y: 341, w: 348, h: 204 },
  jugoslawia: { x: 1101, y: 341, w: 347, h: 204 },
  'wielka-brytania': { x: 201, y: 625, w: 349, h: 203 },
  usa: { x: 651, y: 625, w: 348, h: 203 },
  wlochy: { x: 1101, y: 625, w: 347, h: 203 },
};

/** Apply flag sprite background to an element for a given faction and container size. */
export function applyFlagStyle(el: HTMLElement, factionId: string, w: number, h: number): void {
  const rect = FLAG_RECTS[factionId];
  if (!rect) {
    el.style.backgroundImage = 'none';
    return;
  }
  const scale = Math.max(w / rect.w, h / rect.h);
  const bgW = SHEET_W * scale;
  const bgH = SHEET_H * scale;
  const posX = -(rect.x * scale + (rect.w * scale - w) / 2);
  const posY = -(rect.y * scale + (rect.h * scale - h) / 2);
  el.style.backgroundImage = 'url("flags.png")';
  el.style.backgroundSize = `${bgW.toFixed(1)}px ${bgH.toFixed(1)}px`;
  el.style.backgroundPosition = `${posX.toFixed(1)}px ${posY.toFixed(1)}px`;
  el.style.backgroundRepeat = 'no-repeat';
}

/** Create an HTML element showing a faction flag from the spritesheet. */
export function createFlagElement(factionId: string, width = 64, height = 44): HTMLDivElement {
  const el = document.createElement('div');
  Object.assign(el.style, {
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: '3px',
    flexShrink: '0',
    overflow: 'hidden',
  });
  applyFlagStyle(el, factionId, width, height);
  return el;
}

/**
 * Factions selectable in the lobby. Pozostałe nacje są tymczasowo zablokowane
 * (brak kompletu assetów/bohaterów) — odblokować dorzucając id do tego zbioru.
 */
export const PLAYABLE_FACTION_IDS = new Set<string>(['polska', 'iii-rzesza', 'zsrr']);

/** Factions available for selection (filtered by {@link PLAYABLE_FACTION_IDS}). */
export const PLAYABLE_FACTIONS: Faction[] = FACTIONS.filter((f) => PLAYABLE_FACTION_IDS.has(f.id));

/** Lookup a faction by id. */
export function getFaction(id: string): Faction | undefined {
  return FACTIONS.find((f) => f.id === id);
}

/** Faction-unique units: maps factionId → list of {unitType, building} entries. */
export const FACTION_UNIQUE_UNITS: Record<string, { unitType: UnitType; building: BuildingType }[]> = {
  polska: [
    { unitType: 'polska-ulani', building: 'barracks' },
    { unitType: 'polska-7tp', building: 'vehicle-factory' },
  ],
  zsrr: [
    { unitType: 'zsrr-t34', building: 'vehicle-factory' },
    { unitType: 'zsrr-katiusza', building: 'motor-pool' },
  ],
  'iii-rzesza': [
    { unitType: 'iii-rzesza-tiger', building: 'vehicle-factory' },
    { unitType: 'iii-rzesza-stuka', building: 'airport' },
  ],
  usa: [
    { unitType: 'usa-sherman', building: 'vehicle-factory' },
    { unitType: 'usa-lcvp-higgins', building: 'port' },
  ],
  wlochy: [
    { unitType: 'wlochy-l3-33', building: 'vehicle-factory' },
    { unitType: 'wlochy-sm79-sparviero', building: 'airport' },
  ],
  japonia: [
    { unitType: 'japonia-chi-ha', building: 'vehicle-factory' },
    { unitType: 'japonia-a6m-zero', building: 'airport' },
  ],
  francja: [
    { unitType: 'francja-renault-r35', building: 'vehicle-factory' },
    { unitType: 'francja-ms406', building: 'airport' },
  ],
  jugoslawia: [
    { unitType: 'jugoslawia-partyzanci', building: 'barracks' },
    { unitType: 'jugoslawia-ba64', building: 'motor-pool' },
  ],
  'wielka-brytania': [
    { unitType: 'wielka-brytania-komandosi', building: 'barracks' },
    { unitType: 'wielka-brytania-churchill', building: 'vehicle-factory' },
  ],
};

/**
 * Get the full recruit list for a building, including faction-unique units.
 * Base units from BUILDING_DEFS.recruits + any faction-specific additions.
 */
/** True if the player already fields (or is training) a live copy of `heroType`. */
function playerHasHeroLive(world: TurnWorld, playerId: PlayerId, heroType: UnitType): boolean {
  for (const u of world.units.values()) {
    if (u.owner === playerId && u.type === heroType) return true;
  }
  for (const b of world.buildings.values()) {
    if (b.owner === playerId && b.training?.unitType === heroType) return true;
  }
  return false;
}

export function getRecruitList(
  buildingType: BuildingType,
  factionId?: string,
  world?: TurnWorld,
  playerId?: PlayerId,
): UnitType[] {
  const base = BUILDING_DEFS[buildingType].recruits ?? [];
  if (!factionId) return [...base];
  const extras = FACTION_UNIQUE_UNITS[factionId]
    ?.filter(u => u.building === buildingType)
    .map(u => u.unitType) ?? [];
  const result = [...base, ...extras];

  // Heroes — only at the HQ, the faction's own two characters, and omitted while
  // a live copy already exists for this player (one of each type; respawn = it
  // reappears once the live one dies). Requires world+player to enforce the limit.
  if (buildingType === 'hq' && world && playerId !== undefined) {
    for (const c of ['a', 'b'] as const) {
      const heroType = `hero-${factionId}-${c}` as UnitType;
      if (UNIT_DEFS[heroType] && !playerHasHeroLive(world, playerId, heroType)) {
        result.push(heroType);
      }
    }
  }
  return result;
}
